import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, SlidersHorizontal, PackageOpen } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { LotCard } from "@/components/LotCard";
import { CATEGORIES, MODES, REGIONS } from "@/lib/data";
import { API } from "@/lib/api";

const toggle = (csv, v) => {
  const set = new Set((csv || "").split(",").filter(Boolean));
  set.has(v) ? set.delete(v) : set.add(v);
  return [...set].join(",");
};

const Group = ({ title, items, param, params, setParam }) => (
  <fieldset className="space-y-3">
    <legend className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">{title}</legend>
    {items.map((it) => {
      const checked = (params.get(param) || "").split(",").includes(it.id);
      return (
        <label key={it.id} className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer hover:text-[#05130e]">
          <Checkbox checked={checked} onCheckedChange={() => setParam(param, toggle(params.get(param), it.id))} data-testid={`filter-${param}-${it.id}`} />
          {it.label}
        </label>
      );
    })}
  </fieldset>
);

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [lots, setLots] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    setLots(null);
    axios.get(`${API}/lots`, { params: Object.fromEntries(params) }).then((r) => setLots(r.data)).catch(() => setLots([]));
  }, [params]);

  return (
    <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12" data-testid="marketplace-page">
      <div className="mb-10">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-2">Marketplace</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#05130e]">Lotes de Scrap & Mermas</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="lg:sticky lg:top-24 self-start bg-white border border-slate-200 rounded-2xl p-6 space-y-7" data-testid="marketplace-filters">
          <p className="flex items-center gap-2 font-semibold text-[#05130e]"><SlidersHorizontal className="w-4 h-4" /> Filtros</p>
          <form onSubmit={(e) => { e.preventDefault(); setParam("q", q); }} className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 h-11 focus-within:border-[#00c487]">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => setParam("q", q)} placeholder="Buscar material" className="outline-none text-sm w-full" data-testid="filter-search-input" />
          </form>
          <Group title="Categoría de material" param="category" params={params} setParam={setParam} items={CATEGORIES.map((c) => ({ id: c.id, label: c.short === "RAEE" ? "RAEE y Baterías" : c.short }))} />
          <Group title="Tipo de transacción" param="mode" params={params} setParam={setParam} items={MODES.map((m) => ({ id: m.id, label: m.filter }))} />
          <div className="space-y-3">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Ubicación</p>
            <Select value={params.get("region") || "all"} onValueChange={(v) => setParam("region", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-region-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo Chile</SelectItem>
                {Object.keys(REGIONS).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {[...params.keys()].length > 0 && (
            <button onClick={() => { setQ(""); setParams({}); }} className="text-sm text-[#00915f] font-medium hover:underline" data-testid="filter-clear-btn">Limpiar filtros</button>
          )}
        </aside>
        <section className="lg:col-span-3">
          <p className="text-sm text-slate-500 mb-4" data-testid="marketplace-count">{lots ? `${lots.length} ${lots.length === 1 ? "lote encontrado" : "lotes encontrados"}` : "Cargando lotes..."}</p>
          {lots === null ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div>
          ) : lots.length === 0 ? (
            <div className="text-center py-24 bg-white rounded-2xl border border-dashed border-slate-300" data-testid="marketplace-empty">
              <PackageOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600">No hay lotes con estos filtros.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="marketplace-grid">{lots.map((l, i) => <LotCard key={l.id} lot={l} index={i} />)}</div>
          )}
        </section>
      </div>
    </main>
  );
}
